import React, { useState, useEffect } from 'react';
import { Image, Video, Play, ArrowRight, Mail, X, ChevronLeft, ChevronRight } from 'lucide-react';
import { Link } from 'react-router-dom';
import DOMPurify from 'dompurify';
import { getText, getCurrentLanguage } from '../services/dataSync';
import { listPhotos, listVideos, DEFAULT_BASE_URL } from '../services/api';

const LS_PHOTOS_KEY = 'ifm_admin_photos';
const LS_VIDEOS_KEY = 'ifm_admin_videos';
const readLS = (key) => {
  try { const v = localStorage.getItem(key); return v ? JSON.parse(v) : []; } catch { return []; }
};
const writeLS = (key, val) => { try { localStorage.setItem(key, JSON.stringify(val)); } catch {} };

const GalleryPage = () => {
  const [activeTab, setActiveTab] = useState('photos');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [galleryData, setGalleryData] = useState({ photos: [], videos: [] });
  const [currentLang, setCurrentLang] = useState(getCurrentLanguage());
  const [lightbox, setLightbox] = useState({ open: false, src: '', title: '', currentIndex: -1 });
  const [videoModal, setVideoModal] = useState({ open: false, url: '', title: '' });

  // Use centralized API base URL
  const API_BASE = (DEFAULT_BASE_URL || `${window.location.origin}/api`).replace(/\/$/, '');
  const API_ORIGIN = (() => {
    try { const u = new URL(API_BASE); return `${u.protocol}//${u.host}`; } catch { return window.location.origin; }
  })();

  // Listen for language changes
  useEffect(() => {
    const handleLanguageChange = () => {
      setCurrentLang(getCurrentLanguage());
    };

    // Listen for custom language change events
    window.addEventListener('languageChanged', handleLanguageChange);
    
    // Listen for storage changes (for cross-tab synchronization)
    window.addEventListener('storage', handleLanguageChange);
    
    return () => {
      window.removeEventListener('languageChanged', handleLanguageChange);
      window.removeEventListener('storage', handleLanguageChange);
    };
  }, []);

  const guessIsBareBase64 = (str) => {
    if (!str || typeof str !== 'string') return false;
    if (str.startsWith('data:') || str.startsWith('http') || str.startsWith('blob:') || str.startsWith('/')) return false;
    return /^[A-Za-z0-9+/=]+$/.test(str) && str.length > 100;
  };

  const getImageSrc = (photo) => {
    const raw = (photo && (photo.imageUrl || photo.url)) || '';
        if (process.env.NODE_ENV === 'development') {
          console.log('Gallery - Photo data:', { title: photo?.title, imageUrl: photo?.imageUrl, url: photo?.url, raw });
        }
    if (!raw) return '';
    if (raw.startsWith('http') || raw.startsWith('data:') || raw.startsWith('blob:')) return raw;
    if (raw.startsWith('/')) return `${API_ORIGIN}${raw}`;
    if (guessIsBareBase64(raw)) return `data:image/jpeg;base64,${raw}`;
    return raw;
  };

  const toYouTubeId = (url) => {
    try {
      const u = new URL(url);
      if (u.hostname.includes('youtu.be')) return u.pathname.slice(1);
      if (u.hostname.includes('youtube.com')) return u.searchParams.get('v');
    } catch {}
    return null;
  };

  const toVimeoId = (url) => {
    try {
      const u = new URL(url);
      if (u.hostname.includes('vimeo.com')) {
        const parts = u.pathname.split('/').filter(Boolean);
        return parts[0] || null;
      }
    } catch {}
    return null;
  };

  const getVideoEmbed = (url) => {
    if (!url) return { type: 'unknown', src: '' };
    const yt = toYouTubeId(url);
    if (yt) return { type: 'youtube', src: `https://www.youtube.com/embed/${yt}?autoplay=1&rel=0` };
    const vm = toVimeoId(url);
    if (vm) return { type: 'vimeo', src: `https://player.vimeo.com/video/${vm}?autoplay=1` };
    if (/\.mp4(\?|$)/i.test(url)) return { type: 'mp4', src: url };
    return { type: 'unknown', src: url };
  };

  const getVideoThumb = (video) => {
    if (video?.thumbnailUrl) return video.thumbnailUrl;
    const url = video?.videoUrl || video?.url || '';
    const yt = toYouTubeId(url);
    if (yt) return `https://img.youtube.com/vi/${yt}/hqdefault.jpg`;
    // Optional: custom thumbnails for other platforms can be added here
    return '';
  };

  // removed debug/testing helper functions for production cleanliness

  // Keyboard navigation for lightbox
  useEffect(() => {
    if (!lightbox.open) return;

    const handleKeyDown = (e) => {
      if (e.key === 'ArrowLeft') {
        const currentIndex = lightbox.currentIndex;
        const photos = galleryData.photos;
        if (photos && photos.length > 1) {
          const prevIndex = currentIndex > 0 ? currentIndex - 1 : photos.length - 1;
          const prevPhoto = photos[prevIndex];
          if (prevPhoto && getImageSrc(prevPhoto)) {
            setLightbox({ open: true, src: getImageSrc(prevPhoto), title: prevPhoto.title || '', currentIndex: prevIndex });
          }
        }
      } else if (e.key === 'ArrowRight') {
        const currentIndex = lightbox.currentIndex;
        const photos = galleryData.photos;
        if (photos && photos.length > 1) {
          const nextIndex = currentIndex < photos.length - 1 ? currentIndex + 1 : 0;
          const nextPhoto = photos[nextIndex];
          if (nextPhoto && getImageSrc(nextPhoto)) {
            setLightbox({ open: true, src: getImageSrc(nextPhoto), title: nextPhoto.title || '', currentIndex: nextIndex });
          }
        }
      } else if (e.key === 'Escape') {
        setLightbox({ open: false, src: '', title: '', currentIndex: -1 });
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [lightbox.open, lightbox.currentIndex, galleryData.photos]);

  useEffect(() => {
    // Load gallery data from backend API
    const loadGallery = async () => {
      try {
        const [photos, videos] = await Promise.all([listPhotos(), listVideos()]);
        const ph = Array.isArray(photos) ? photos : [];
        const vd = Array.isArray(videos) ? videos : [];
        const mergeLocal = process.env.NODE_ENV === 'development';
        const lsPh = mergeLocal ? readLS(LS_PHOTOS_KEY) : [];
        const lsVd = mergeLocal ? readLS(LS_VIDEOS_KEY) : [];
        // Avoid duplicates based on _id or title+imageUrl
        const merge = (a, b) => {
          const seen = new Set();
          const key = (x) => x._id || `${x.title}|${x.imageUrl || x.url}`;
          const out = [];
          [...a, ...b].forEach((x) => { const k = key(x); if (!seen.has(k)) { seen.add(k); out.push(x); } });
          return out;
        };
        const mergedPhotos = merge(ph, lsPh);
        const mergedVideos = merge(vd, lsVd);
        if (process.env.NODE_ENV === 'development') {
          console.log('Gallery - Loaded data:', { 
            backendPhotos: ph.length, 
            localStoragePhotos: lsPh.length, 
            mergedPhotos: mergedPhotos.length,
            backendVideos: vd.length,
            localStorageVideos: lsVd.length,
            mergedVideos: mergedVideos.length
          });
        }
        setGalleryData({
          photos: mergedPhotos,
          videos: mergedVideos
        });
      } catch (error) {
        if (process.env.NODE_ENV === 'development') {
          console.error('Gallery - Error loading:', error);
        }
        const fallback =
          process.env.NODE_ENV === 'development'
            ? { photos: readLS(LS_PHOTOS_KEY), videos: readLS(LS_VIDEOS_KEY) }
            : { photos: [], videos: [] };
        setGalleryData(fallback);
      }
    };
    
    loadGallery();
    
    // Listen for data changes from admin panel
    const handleDataChange = (event) => {
      if (event.detail && event.detail.type === 'gallery') {
        if (process.env.NODE_ENV === 'development') {
          console.log('Gallery data changed, reloading...');
        }
        loadGallery();
      }
    };
    
    // Listen for custom data change events
    window.addEventListener('dataChanged', handleDataChange);
    
    // Set up interval to refresh data (every 10 seconds)
    const interval = setInterval(loadGallery, 10000);
    
    return () => {
      clearInterval(interval);
      window.removeEventListener('dataChanged', handleDataChange);
    };
  }, []);

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-100 via-white to-blue-50">
      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-primary-600 via-primary-700 to-primary-800 text-white overflow-hidden pt-6">
        <div className="container relative z-10 py-8 md:py-12">
          <div className="text-center max-w-4xl mx-auto">
            <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold mb-4 leading-tight">
              {getText('gallery.title')}
            </h1>
            <p className="text-base md:text-lg lg:text-xl text-blue-100 leading-relaxed max-w-3xl mx-auto">
              {getText('gallery.subtitle')}
            </p>
          </div>
        </div>
      </section>

      {/* Tab Navigation */}
      <section className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-start">
            <div className="flex space-x-8">
              {[
                { id: 'photos', name: getText('gallery.tab.photos'), icon: Image },
                { id: 'videos', name: getText('gallery.tab.videos'), icon: Video }
              ].map((tab) => {
                const Icon = tab.icon;
                return (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    className={`flex items-center py-4 px-1 border-b-2 font-medium text-sm ${
                      activeTab === tab.id
                        ? 'border-primary-500 text-primary-600'
                        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                    }`}
                  >
                    <Icon className="h-4 w-4 mr-2" />
                    {tab.name}
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      {/* Gallery Content */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Content */}
          
          {activeTab === 'photos' ? (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {galleryData.photos && galleryData.photos.length > 0 ? (
                galleryData.photos.map((photo, index) => (
                  <div key={photo._id || photo.id || `${photo.title}|${photo.imageUrl || photo.url}`} className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
                    <div className="aspect-w-16 aspect-h-9 bg-gray-200">
                      {getImageSrc(photo) ? (
                        <img 
                          src={getImageSrc(photo)} 
                          alt={photo.title || getText('gallery.photo.defaultTitle')} 
                          className="w-full h-48 object-cover cursor-pointer"
                          onClick={() => setLightbox({ open: true, src: getImageSrc(photo), title: photo.title, currentIndex: index })}
                          onError={(e) => {
                            if (process.env.NODE_ENV === 'development') {
                              console.error('Image failed to load:', getImageSrc(photo));
                            }
                            e.target.style.display = 'none';
                            e.target.nextElementSibling.style.display = 'flex';
                          }}
                        />
                      ) : null}
                      <div className="w-full h-48 bg-gradient-to-br from-primary-100 to-primary-200 flex items-center justify-center" style={{ display: getImageSrc(photo) ? 'none' : 'flex' }}>
                        <Image className="h-16 w-16 text-primary-600" />
                      </div>
                    </div>
                    <div className="p-6">
                      <h3 className="text-lg font-semibold text-gray-900 mb-2">{photo.title || getText('gallery.photo.untitled')}</h3>
                      <div className="text-gray-600 text-sm mb-3" dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(photo.description || getText('gallery.photo.noDescription')) }} />
                      {/* Removed external open link as requested */}
                      <div className="flex flex-wrap gap-2">
                        {Array.isArray(photo.tags) 
                          ? photo.tags.map((tag) => (
                              <span key={tag} className="text-xs bg-gray-100 text-gray-600 px-2 py-1 rounded-full">
                                {tag}
                              </span>
                            ))
                          : photo.tags && photo.tags.split(',').map((tag) => (
                              <span key={tag.trim()} className="text-xs bg-gray-100 text-gray-600 px-2 py-1 rounded-full">
                                {tag.trim()}
                              </span>
                            ))
                        }
                      </div>
                    </div>
                  </div>
                ))
              ) : (
                <div className="col-span-full text-center py-12">
                  <Image className="h-16 w-16 text-gray-400 mx-auto mb-4" />
                  <h3 className="text-lg font-medium text-gray-900 mb-2">{getText('gallery.noPhotos')}</h3>
                  <p className="text-gray-500">{getText('gallery.noPhotosDesc')}</p>
                </div>
              )}
            </div>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {galleryData.videos && galleryData.videos.length > 0 ? (
                galleryData.videos.map((video) => {
                  const url = video.videoUrl || video.url || '';
                  const embed = getVideoEmbed(url);
                  const thumb = getVideoThumb(video);
                  return (
                    <div key={video._id || video.id || `${video.title}|${url}`} className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
                      <div className="aspect-w-16 aspect-h-9 bg-gray-200 relative cursor-zoom-in group" onClick={() => setVideoModal({ open: true, url, title: video.title })}>
                        {thumb ? (
                          <img src={thumb} alt={video.title || getText('gallery.video.defaultTitle')} className="w-full h-48 object-cover" />
                        ) : (
                          <div className="w-full h-48 bg-gradient-to-br from-primary-100 to-primary-200 flex items-center justify-center">
                            <Play className="h-16 w-16 text-primary-600" />
                          </div>
                        )}
                        <div className="absolute inset-0 bg-black/30 opacity-0 group-hover:opacity-100 transition-opacity duration-200 flex items-center justify-center">
                          <div className="w-16 h-16 rounded-full bg-white/90 flex items-center justify-center shadow-lg">
                            <Play className="h-8 w-8 text-primary-700" />
                          </div>
                        </div>
                        <div className="absolute bottom-2 right-2 bg-black bg-opacity-75 text-white text-xs px-2 py-1 rounded">
                          {video.duration || (embed.type.toUpperCase())}
                        </div>
                      </div>
                      <div className="p-6">
                        <h3 className="text-lg font-semibold text-gray-900 mb-2">{video.title || getText('gallery.video.untitled')}</h3>
                        <div className="text-gray-600 text-sm mb-3" dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(video.description || getText('gallery.video.noDescription')) }} />
                        <span className="text-xs bg-blue-100 text-blue-800 px-2 py-1 rounded-full">
                          {video.category || getText('gallery.category.uncategorized')}
                        </span>
                      </div>
                    </div>
                  );
                })
              ) : (
                <div className="col-span-full text-center py-12">
                  <Video className="h-16 w-16 text-gray-400 mx-auto mb-4" />
                  <h3 className="text-lg font-medium text-gray-900 mb-2">{getText('gallery.noVideos')}</h3>
                  <p className="text-gray-500">{getText('gallery.noVideosDesc')}</p>
                </div>
              )}
            </div>
          )}
        </div>
    {lightbox.open && (
      <div
        className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4"
        onClick={() => setLightbox({ open: false, src: '', title: '', currentIndex: -1 })}
      >
        <button
          type="button"
          className="absolute top-4 right-4 text-white bg-black/70 hover:bg-black/90 rounded-full p-2 shadow-lg z-10"
          aria-label={getText('gallery.aria.closeImage')}
          onClick={(e) => {
            e.stopPropagation();
            setLightbox({ open: false, src: '', title: '', currentIndex: -1 });
          }}
        >
          <X className="h-5 w-5" />
        </button>
        
        {/* Previous Button */}
        {galleryData.photos && galleryData.photos.length > 1 && (
          <button
            type="button"
            className="absolute left-4 top-1/2 -translate-y-1/2 text-white bg-black/70 hover:bg-black/90 rounded-full p-3 shadow-lg z-10 transition-all duration-200 hover:scale-110"
            aria-label={getText('gallery.aria.previousImage')}
            onClick={(e) => {
              e.stopPropagation();
              const currentIndex = lightbox.currentIndex;
              const photos = galleryData.photos;
              const prevIndex = currentIndex > 0 ? currentIndex - 1 : photos.length - 1;
              const prevPhoto = photos[prevIndex];
              if (prevPhoto && getImageSrc(prevPhoto)) {
                setLightbox({ open: true, src: getImageSrc(prevPhoto), title: prevPhoto.title || '', currentIndex: prevIndex });
              }
            }}
          >
            <ChevronLeft className="h-6 w-6" />
          </button>
        )}

        {/* Next Button */}
        {galleryData.photos && galleryData.photos.length > 1 && (
          <button
            type="button"
            className="absolute right-4 top-1/2 -translate-y-1/2 text-white bg-black/70 hover:bg-black/90 rounded-full p-3 shadow-lg z-10 transition-all duration-200 hover:scale-110"
            aria-label={getText('gallery.aria.nextImage')}
            onClick={(e) => {
              e.stopPropagation();
              const currentIndex = lightbox.currentIndex;
              const photos = galleryData.photos;
              const nextIndex = currentIndex < photos.length - 1 ? currentIndex + 1 : 0;
              const nextPhoto = photos[nextIndex];
              if (nextPhoto && getImageSrc(nextPhoto)) {
                setLightbox({ open: true, src: getImageSrc(nextPhoto), title: nextPhoto.title || '', currentIndex: nextIndex });
              }
            }}
          >
            <ChevronRight className="h-6 w-6" />
          </button>
        )}

        <div className="max-w-[90vw] max-h-[85vh]" onClick={(e) => e.stopPropagation()}>
          <img
            src={lightbox.src}
            alt={lightbox.title}
            className="max-w-full max-h-[85vh] object-contain cursor-zoom-out rounded-lg shadow-2xl bg-black/40"
          />
          {lightbox.title && (
            <div className="absolute bottom-4 left-1/2 -translate-x-1/2 bg-black/70 text-white px-4 py-2 rounded-lg text-sm max-w-[90vw] text-center">
              {lightbox.title}
            </div>
          )}
        </div>
      </div>
    )}
    {videoModal.open && (
      <div
        className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4"
        onClick={() => setVideoModal({ open: false, url: '', title: '' })}
      >
        <button
          type="button"
          className="absolute top-4 right-4 text-white bg-black/70 hover:bg-black/90 rounded-full p-2 shadow-lg"
          aria-label={getText('gallery.aria.closeVideo')}
          onClick={() => setVideoModal({ open: false, url: '', title: '' })}
        >
          <X className="h-5 w-5" />
        </button>
        <div
          className="w-[90vw] h-[60vh] max-w-5xl bg-black rounded-lg overflow-hidden shadow-2xl"
          onClick={(e) => e.stopPropagation()}
        >
          {(() => {
            const embed = getVideoEmbed(videoModal.url);
            if (embed.type === 'youtube' || embed.type === 'vimeo') {
              return (
                <iframe
                  src={embed.src}
                  title={videoModal.title}
                  className="w-full h-full"
                  frameBorder="0"
                  allow="autoplay; fullscreen; picture-in-picture"
                  allowFullScreen
                />
              );
            }
            if (embed.type === 'mp4') {
              return <video src={embed.src} className="w-full h-full" controls autoPlay />;
            }
            return (
              <div className="w-full h-full flex items-center justify-center text-white">
                {getText('gallery.video.unsupported')}
              </div>
            );
          })()}
        </div>
      </div>
    )}
      </section>

    </div>
  );
};

export default GalleryPage;
